/**
 * ColorPicker.tsx — Compact color picker with presets, recent colors, hex input, and opacity.
 *
 * Layout fits in 220px width:
 *   - 10 preset swatches (DRAW_COLORS)
 *   - Recent colors row (last 5)
 *   - Hex input field
 *   - Opacity slider (0–100%)
 */

import { useState, useCallback } from "react";
import { DRAWING_COLORS } from "@/types/chart";
import { useDrawingStore } from "@/stores/drawingStore";
import { cn } from "@/lib/utils";

interface ColorPickerProps {
  value: string;
  onChange: (color: string) => void;
  opacity?: number;
  onOpacityChange?: (opacity: number) => void;
  showOpacity?: boolean;
  className?: string;
}

export function ColorPicker({
  value,
  onChange,
  opacity,
  onOpacityChange,
  showOpacity = true,
  className,
}: ColorPickerProps) {
  const recentColors = useDrawingStore((state) => state.recentColors);
  const addRecentColor = useDrawingStore((state) => state.addRecentColor);
  const [hexInput, setHexInput] = useState(value.toUpperCase());

  const handleColorSelect = useCallback(
    (color: string) => {
      onChange(color);
      addRecentColor(color);
      setHexInput(color.toUpperCase());
    },
    [onChange, addRecentColor],
  );

  const handleHexBlur = useCallback(() => {
    const cleaned = hexInput.trim().replace(/[^0-9A-Fa-f#]/g, "");
    const hex = cleaned.startsWith("#") ? cleaned : `#${cleaned}`;
    if (/^#[0-9A-Fa-f]{6}$/.test(hex)) {
      handleColorSelect(hex.toUpperCase());
    } else {
      setHexInput(value.toUpperCase());
    }
  }, [hexInput, value, handleColorSelect]);

  const swatchSize = "h-5 w-5";

  return (
    <div className={cn("w-[220px] space-y-2.5", className)}>
      {/* Preset swatches */}
      <div className="flex flex-wrap gap-1.5">
        {DRAWING_COLORS.map((color) => (
          <button
            key={color}
            type="button"
            onClick={() => handleColorSelect(color)}
            className={cn(swatchSize, "rounded-full flex-shrink-0")}
            style={{
              backgroundColor: color,
              boxShadow:
                value.toUpperCase() === color.toUpperCase()
                  ? "0 0 0 1.5px var(--text-primary)"
                  : "inset 0 0 0 0.5px var(--border-muted)",
            }}
            aria-label={`Color ${color}`}
          />
        ))}
      </div>

      {/* Recent colors */}
      {recentColors.length > 0 && (
        <div>
          <p
            className="mb-1"
            style={{
              color: "var(--text-muted)",
              fontSize: 10,
              textTransform: "uppercase",
              letterSpacing: "0.04em",
            }}
          >
            Recent
          </p>
          <div className="flex gap-1.5">
            {recentColors.slice(0, 5).map((color) => (
              <button
                key={color}
                type="button"
                onClick={() => handleColorSelect(color)}
                className={cn(swatchSize, "rounded-full flex-shrink-0")}
                style={{
                  backgroundColor: color,
                  boxShadow:
                    value.toUpperCase() === color.toUpperCase()
                      ? "0 0 0 1.5px var(--text-primary)"
                      : "inset 0 0 0 0.5px var(--border-muted)",
                }}
                aria-label={`Recent color ${color}`}
              />
            ))}
          </div>
        </div>
      )}

      {/* Hex input */}
      <div className="flex items-center gap-2">
        <div
          className="h-7 w-7 rounded-md border flex-shrink-0"
          style={{
            backgroundColor: value,
            borderColor: "var(--border-default)",
          }}
        />
        <input
          type="text"
          value={hexInput}
          onChange={(e) => setHexInput(e.target.value)}
          onBlur={handleHexBlur}
          onKeyDown={(e) => {
            if (e.key === "Enter") handleHexBlur();
          }}
          className="flex-1 h-7 rounded-md border px-2 text-xs font-mono"
          style={{
            backgroundColor: "var(--bg-base)",
            borderColor: "var(--border-default)",
            color: "var(--text-primary)",
          }}
          placeholder="#FFFFFF"
          maxLength={7}
        />
      </div>

      {/* Opacity slider */}
      {showOpacity && opacity !== undefined && onOpacityChange && (
        <div>
          <div className="flex items-center justify-between mb-1">
            <span
              style={{
                color: "var(--text-muted)",
                fontSize: 10,
                textTransform: "uppercase",
                letterSpacing: "0.04em",
              }}
            >
              Opacity
            </span>
            <span
              style={{
                color: "var(--text-secondary)",
                fontSize: 11,
              }}
            >
              {opacity}%
            </span>
          </div>
          <input
            type="range"
            min={10}
            max={100}
            step={5}
            value={opacity}
            onChange={(e) => onOpacityChange(Number(e.target.value))}
            className="w-full h-1.5 rounded-full appearance-none cursor-pointer"
            style={{
              backgroundColor: "var(--border-default)",
              accentColor: "var(--accent-primary)",
            }}
          />
        </div>
      )}
    </div>
  );
}
